import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveTopicFromRoute, helpTopics } from '../../help';

interface HelpContextType {
  isOpen: boolean;
  currentTopic: string;
  openHelp: (topic?: string) => void;
  closeHelp: () => void;
  setActiveHelpView: (view: string | null) => void;
}

const HelpContext = createContext<HelpContextType | null>(null);

export function HelpProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [manualTopic, setManualTopic] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<string | null>(null);

  const resolvedTopic = resolveTopicFromRoute(location.pathname, activeView);
  const currentTopic = manualTopic || resolvedTopic;

  const openHelp = useCallback((topic?: string) => {
    if (topic && helpTopics[topic]) {
      setManualTopic(topic);
    } else {
      setManualTopic(null);
    }
    setIsOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setIsOpen(false);
    setManualTopic(null);
  }, []);

  const setActiveHelpView = useCallback((view: string | null) => {
    setActiveView(view);
  }, []);

  const ctxValue = useMemo(
    () => ({ isOpen, currentTopic, openHelp, closeHelp, setActiveHelpView }),
    [isOpen, currentTopic, openHelp, closeHelp, setActiveHelpView]
  );

  return (
    <HelpContext.Provider value={ctxValue}>
      {children}
    </HelpContext.Provider>
  );
}

export function useHelp() {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
}
